import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ApiResponse, PaginatedResponse } from '../interfaces/api.interface';
import { Role, IRole } from '../models/role.model';
import { AuthService } from './auth.service';

export interface RoleFilters {
  name?: string;
  roleType?: string;
  companyId?: number;
  isActive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AdminRolesService {
  private baseUrl = '/api/roles';
  
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}
  
  /**
   * Get all roles with optional filters
   */
  getRoles(filters?: RoleFilters): Observable<ApiResponse<Role[]>> {
    let params = new HttpParams();
    
    if (filters) {
      if (filters.name) params = params.set('name', filters.name);
      if (filters.roleType) params = params.set('roleType', filters.roleType);
      if (filters.companyId) params = params.set('companyId', filters.companyId.toString());
      if (filters.isActive !== undefined) params = params.set('isActive', filters.isActive.toString());
    }
    
    return this.http.get<ApiResponse<IRole[]>>(this.baseUrl + '/all', { params }).pipe(
      map(response => ({
        ...response,
        data: response.data?.map((role: IRole) => new Role(role)) || []
      }))
    );
  }
  
  /**
   * Get roles with pagination
   */
  getRolesPaginated(page = 1, limit = 10, filters?: RoleFilters): Observable<PaginatedResponse<Role>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (filters) {
      if (filters.name) params = params.set('name', filters.name);
      if (filters.roleType) params = params.set('roleType', filters.roleType);
      if (filters.companyId) params = params.set('companyId', filters.companyId.toString());
      if (filters.isActive !== undefined) params = params.set('isActive', filters.isActive.toString());
    }
    
    return this.http.get<PaginatedResponse<IRole>>(this.baseUrl, { params }).pipe(
      map(response => ({
        ...response,
        data: response.data?.map((role: IRole) => new Role(role)) || []
      }))
    );
  }
  
  /**
   * Get role by ID
   */
  getRoleById(id: number): Observable<ApiResponse<Role | null>> {
    return this.http.get<ApiResponse<IRole>>(`${this.baseUrl}/${id}`).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Role(response.data) : null
      }))
    );
  }
  
  /**
   * Create new role
   */
  createRole(role: Partial<IRole>): Observable<ApiResponse<Role | null>> {
    return this.http.post<ApiResponse<IRole>>(this.baseUrl, role).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Role(response.data) : null
      }))
    );
  }
  
  /**
   * Update role
   */
  updateRole(id: number, role: Partial<IRole>): Observable<ApiResponse<Role | null>> {
    return this.http.put<ApiResponse<IRole>>(`${this.baseUrl}/${id}`, role).pipe(
      map(response => ({
        ...response,
        data: response.data ? new Role(response.data) : null
      }))
    );
  }
  
  /**
   * Delete role
   */
  deleteRole(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Get roles filtered by current user's company type
   * Only returns roles that are compatible with the user's company type
   */
  getRolesByCompanyType(filters?: RoleFilters): Observable<ApiResponse<Role[]>> {
    return this.authService.currentUser$.pipe(
      take(1),
      switchMap(user => {
        // Get all roles first
        return this.getRoles(filters).pipe(
          map(response => {
            if (!response.data || !user?.company?.type) {
              return response;
            }

            const userCompanyType = user.company.type;
            
            // Filter roles by company type compatibility
            const filteredRoles = response.data.filter(role => {
              return role.isCompatibleWithCompanyType(userCompanyType);
            });

            return {
              ...response,
              data: filteredRoles
            };
          })
        );
      })
    );
  }

  /**
   * Get roles for dropdown/selection filtered by current user's company type
   * Returns simplified role list suitable for form selectors
   */
  getRolesForSelection(entityType?: 'driver' | 'client' | 'employee'): Observable<Role[]> {
    const filters: RoleFilters = {};
    
    // Apply entity type filter if provided
    if (entityType) {
      filters.roleType = 'user'; // Only 'user' roles for selection
    }

    return this.getRolesByCompanyType(filters).pipe(
      map(response => {
        if (!response.data) return [];

        let roles = response.data;

        // Additional filtering by entity type if needed
        if (entityType) {
          // TODO: Add specific filtering based on role permissions or naming conventions
          // This could be based on role names, permissions, or other criteria
          // For now, return all compatible roles
        }

        return roles.filter(role => role.isActive);
      })
    );
  }
}
